class AudioPlayer extends HTMLElement {
    constructor() {
      super();
      this.audio =
        this.querySelector('.voice-audio') ||
        this.querySelector('.audio-player-element') ||
        this.querySelector('audio');
      this.playBtn = this.querySelector('.voice-play') || this.querySelector('.audio-player-play-btn');
      this.iconPath = this.querySelector('.voice-icon-path');
      this.barsContainer = this.querySelector('.voice-bars') || this.querySelector('.audio-player-waveform');
      this.timeDisplay = this.querySelector('.voice-time') || this.querySelector('.audio-player-time');
      this.isPlaying = false;
      this.duration = 0;
      this.animationFrameId = null;
      this.bars = [];
      this.pattern = [2, 3, 1, 2, 4, 1, 3, 2, 1, 3, 2, 4, 1, 2, 3, 1];
      this.hasInitialized = false;
      this.resizeObserver = null;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.stopAnimation();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }
    
    init() {
      if (this.hasInitialized) return;
      if (!this.audio || !this.playBtn || !this.barsContainer || !this.timeDisplay) return;
      this.hasInitialized = true;

      this.generateBars();

      this.playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggle();
      });

      this.audio.addEventListener('loadedmetadata', () => {
        if (!isNaN(this.audio.duration)) {
          this.duration = this.audio.duration;
          this.updateTimeDisplay(this.duration);
          this.updateProgress(0);
        }
      });

      this.audio.addEventListener('timeupdate', () => {
        if (!isNaN(this.audio.duration) && this.audio.duration > 0) {
          const percent = (this.audio.currentTime / this.audio.duration) * 100;
          this.updateProgress(percent);
          this.updateTimeDisplay(Math.max(0, this.audio.duration - this.audio.currentTime));
        }
      });

      this.audio.addEventListener('play', () => {
        this.isPlaying = true;
        this.setIconPaused(true);
        this.animate();
      });

      this.audio.addEventListener('pause', () => {
        this.isPlaying = false;
        this.setIconPaused(false);
        this.stopAnimation();
      });

      this.audio.addEventListener('ended', () => {
        this.audio.currentTime = 0;
        this.isPlaying = false;
        this.setIconPaused(false);
        this.updateProgress(0);
        this.updateTimeDisplay(this.duration || 0);
        this.stopAnimation();
      });

      this.barsContainer.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isNaN(this.audio.duration) || this.audio.duration <= 0) return;
        const rect = this.barsContainer.getBoundingClientRect();
        let x = e.clientX - rect.left;
        if (x < 0) x = 0;
        if (x > rect.width) x = rect.width;
        const pos = rect.width === 0 ? 0 : x / rect.width;
        this.audio.currentTime = pos * this.audio.duration;
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.generateBars();
          if (!isNaN(this.audio.duration) && this.audio.duration > 0) {
            const percent = (this.audio.currentTime / this.audio.duration) * 100;
            this.updateProgress(percent);
          } else {
            this.updateProgress(0);
          }
        });
        this.resizeObserver.observe(this.barsContainer);
      }
    }

    generateBars() {
      this.barsContainer.innerHTML = '';
      this.bars = [];
      const containerWidth = this.barsContainer.clientWidth || parseFloat(getComputedStyle(this.barsContainer).width) || 0;
      const barWidth = 2;
      const gap = 2;
      const derivedCount = Math.floor((containerWidth + gap) / (barWidth + gap));
      const targetCount = Math.max(30, Math.min(120, derivedCount || 95));

      for (let i = 0; i < targetCount; i++) {
        const bar = document.createElement('div');
        bar.className = 'voice-bar';
        const heightMultiplier = this.pattern[i % this.pattern.length];
        bar.style.height = `${heightMultiplier * 4 + 2}px`;
        this.barsContainer.appendChild(bar);
        this.bars.push(bar);
      }
    }
    
    updateProgress(percent) {
      const totalBars = this.bars.length;
      if (!totalBars) return;
      const clamped = Math.max(0, Math.min(100, percent));
      const activeIndex = Math.floor((clamped / 100) * totalBars);
      for (let i = 0; i < totalBars; i++) {
        this.bars[i].classList.toggle('played', i < activeIndex);
      }
    }
    
    animate() {
      if (!this.isPlaying) return;
      if (!isNaN(this.audio.duration) && this.audio.duration > 0) {
        const percent = (this.audio.currentTime / this.audio.duration) * 100;
        this.updateProgress(percent);
        this.updateTimeDisplay(Math.max(0, this.audio.duration - this.audio.currentTime));
      }
      this.animationFrameId = requestAnimationFrame(() => this.animate());
    }
    
    toggle() {
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    }

    play() {
      document.querySelectorAll('audio-player-component').forEach(player => {
        if (player !== this && player.pause) player.pause();
      });

      const p = this.audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    
    pause() {
      this.audio.pause();
      this.isPlaying = false;
    }
    
    updateTimeDisplay(time) {
      if (isNaN(time)) return;
      this.timeDisplay.textContent = this.formatTime(time);
    }

    formatTime(totalSeconds) {
      const safe = Math.max(0, Math.floor(totalSeconds));
      const minutes = Math.floor(safe / 60);
      const seconds = safe % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    setIconPaused(paused) {
      if (!this.iconPath) return;
      if (paused) {
        this.iconPath.setAttribute('d', 'M2 1h3v12H2V1zm5 0h3v12H7V1z');
      } else {
        this.iconPath.setAttribute('d', 'M1 1l10 6-10 6V1z');
      }
    }

    stopAnimation() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    }
  }
  
  customElements.define('audio-player-component', AudioPlayer);
